import React, { useEffect, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type LensGrain, grainsForType, parseGrain } from '../lensGrain';

import { AGGREGATE_OPTIONS, SHELF_LABELS, type ShelfKey } from './panelTypes';
import type { PanelState } from './usePanelState';

const GRAIN_LABEL: Record<LensGrain, string> = {
  day: 'Day',
  hour: 'Hour',
  minute: 'Minute',
  month: 'Month',
  quarter: 'Quarter',
  second: 'Second',
  week: 'Week',
  year: 'Year',
};

/** What a filled chip's own menu offers, decided once per render from the field it holds. */
type ChipMenu =
  | { kind: 'grain'; current: LensGrain }
  | { kind: 'aggregate'; locked?: 'calculated' | 'countDistinct' }
  | { kind: 'ratio' };

/**
 * One shelf row: an 84px caption, then the chip area. A single-value shelf
 * (x, series, value) shows one chip whose own menu holds that field's
 * modifier: a date's grain, a measure's aggregate, or the fixed word for a
 * ratio or calculated field. The many-valued Columns shelf just lists plain
 * chips, one per column, with no modifier menu. Split out of Shelves.tsx per
 * this repo's one-component-per-file rule.
 */
export default function ShelfChip({
  disabled,
  onMenuOpenChange,
  shelfKey,
  state,
}: {
  disabled?: boolean;
  /** Lets the panel hold Escape from closing while this chip's own menu is open. */
  onMenuOpenChange?: (open: boolean) => void;
  shelfKey: ShelfKey;
  state: PanelState;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const values = state.shelfValues(shelfKey);
  const isTarget = state.target === shelfKey;
  const bad = state.shelfBad(shelfKey);
  const tooltip = state.shelfFixTooltip(shelfKey);
  const many = shelfKey === 'values';
  const [dragOver, setDragOver] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    onMenuOpenChange?.(Boolean(menuAnchor));
    // onMenuOpenChange is a hand-off, not a dependency: only this chip's own
    // menu opening or closing should notify the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuAnchor]);

  // A governed ratio measure clears view.value (there is no single column to name), so the
  // Measure shelf shows the ratio's own label instead of an empty "Add field" pill.
  const ratio = shelfKey === 'value' ? state.draft.view.ratio : undefined;
  const ratioField = ratio
    ? state.fields.find((field) => field.ratio?.of === ratio.of && field.ratio?.per === ratio.per)
    : undefined;
  const singleValue = !many && !ratio ? values[0] : undefined;
  // A date shelf can hold a grain expression ("week(transaction_date_sydt)") rather than the
  // bare column, so the field lookup goes through parseGrain first: matching on the raw
  // expression finds nothing (no field is ever named that), which silently drops both the chip's
  // nice label and its whole grain menu the moment any grain other than the bare column is picked.
  const singleColumn = singleValue ? (parseGrain(singleValue)?.column ?? singleValue) : undefined;
  const singleField = singleColumn ? state.fields.find((field) => field.name === singleColumn) : undefined;

  const menu: ChipMenu | undefined = ratio
    ? { kind: 'ratio' }
    : singleField?.kind === 'date'
      ? { current: parseGrain(singleValue)?.grain ?? 'day', kind: 'grain' }
      : shelfKey === 'value' && singleField?.kind === 'calculated'
        ? { kind: 'aggregate', locked: 'calculated' }
        : shelfKey === 'value' && singleField?.aggregate === 'countDistinct'
          ? { kind: 'aggregate', locked: 'countDistinct' }
          : shelfKey === 'value' && singleField
            ? { kind: 'aggregate' }
            : undefined;

  const currentAggregate = state.draft.view.aggregate ?? 'sum';
  const modifier = ratio
    ? 'Ratio'
    : menu?.kind === 'grain'
      ? GRAIN_LABEL[menu.current]
      : menu?.kind === 'aggregate'
        ? menu.locked === 'calculated'
          ? 'Sum'
          : menu.locked === 'countDistinct'
            ? 'Count distinct'
            : (AGGREGATE_OPTIONS.find((option) => option.value === currentAggregate)?.label ?? 'Sum')
        : undefined;

  const chipLabel = ratio ? (ratioField?.label ?? ratioField?.name ?? ratio.of) : (singleField?.label ?? singleValue);

  const active = dragOver || isTarget;

  const body = (
    <Box
      onDragLeave={() => setDragOver(false)}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        const name = event.dataTransfer.getData('text/plain');
        const field = state.fields.find((entry) => entry.name === name);
        if (field) {
          state.assignTo(shelfKey, field);
        }
      }}
      sx={{ alignItems: 'center', display: 'flex', gap: 1, minHeight: 36, py: 0.25 }}
    >
      <Typography color="text.secondary" sx={{ flexShrink: 0, width: 84 }} variant="caption">
        {SHELF_LABELS[shelfKey]}
      </Typography>
      <Box
        aria-label={`${SHELF_LABELS[shelfKey]} shelf`}
        aria-pressed={isTarget}
        onClick={disabled ? undefined : () => state.setTarget(shelfKey)}
        onKeyDown={
          disabled
            ? undefined
            : (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  state.setTarget(shelfKey);
                }
              }
        }
        role="button"
        tabIndex={disabled ? -1 : 0}
        sx={{
          alignItems: 'center',
          // A single `border` declaration, never a separate borderColor/borderStyle alongside it:
          // those apply as later, independent CSS declarations that only override their own
          // sub-property, so an unconditional borderStyle sitting next to a conditional
          // `border: 'none'` re-enables the border's visibility while its width and colour fall
          // back to the browser's own defaults (medium ~3px, currentColor) - a real defect this
          // caught, not a hypothetical one.
          border: active
            ? `1.5px ${dragOver ? 'dashed' : 'solid'} ${tokens.accent}`
            : bad
              ? `1px solid ${theme.palette.error.main}`
              : 'none',
          borderRadius: '8px',
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          flex: 1,
          flexWrap: 'wrap',
          gap: 0.5,
          minWidth: 0,
          // This has to be role="button" on a div rather than a real <button> (component="button")
          // because it hosts a nested Chip, itself a button once it has an onClick: a real button
          // cannot legally contain another one. A plain div with role="button" falls outside this
          // codebase's usual button focus-reset, so it needs its own explicit one, replaced with an
          // on-brand ring rather than removed outright (silently dropping it would leave a keyboard
          // user with no visible indicator of which row currently has focus).
          outline: 'none',
          p: active || bad ? 0.5 : 0,
          '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
        }}
      >
        {values.length === 0 && !ratio && (
          <Chip
            label="Add field"
            size="small"
            sx={{
              bgcolor: 'transparent',
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
              color: 'text.secondary',
              height: 28,
              pointerEvents: 'none',
            }}
            variant="outlined"
          />
        )}
        {many &&
          values.map((value) => (
            <Chip
              deleteIcon={<CloseIcon fontSize="small" />}
              key={value}
              label={state.fields.find((field) => field.name === value)?.label ?? value}
              onDelete={(event) => {
                event.stopPropagation();
                state.clearValueAt(value);
              }}
              sx={{ height: 28, maxWidth: '100%' }}
              title={state.fields.find((field) => field.name === value)?.label ?? value}
              variant="outlined"
            />
          ))}
        {!many && (ratio || singleValue) && (
          <Chip
            aria-haspopup={menu ? 'menu' : undefined}
            color={bad ? 'error' : 'default'}
            deleteIcon={<CloseIcon fontSize="small" />}
            disabled={disabled}
            label={modifier ? `${chipLabel} · ${modifier}` : chipLabel}
            onClick={
              menu
                ? (event) => {
                    event.stopPropagation();
                    setMenuAnchor(event.currentTarget);
                  }
                : undefined
            }
            onDelete={(event) => {
              event.stopPropagation();
              state.clear(shelfKey);
            }}
            sx={{ height: 28, maxWidth: '100%' }}
            title={chipLabel}
            variant={bad ? 'filled' : 'outlined'}
          />
        )}
      </Box>
      <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} open={Boolean(menuAnchor)}>
        {menu?.kind === 'grain' &&
          grainsForType(singleField?.type).map((grain) => (
            <MenuItem
              key={grain}
              onClick={() => {
                state.setGrain(shelfKey, grain);
                setMenuAnchor(null);
              }}
              selected={grain === menu.current}
            >
              <ListItemIcon>
                {grain === menu.current && <CheckIcon fontSize="small" sx={{ color: tokens.accent }} />}
              </ListItemIcon>
              <ListItemText>{GRAIN_LABEL[grain]}</ListItemText>
            </MenuItem>
          ))}
        {menu?.kind === 'ratio' && (
          <MenuItem disabled>
            <ListItemText>Set by the dataset</ListItemText>
          </MenuItem>
        )}
        {menu?.kind === 'aggregate' && menu.locked === 'calculated' && (
          <MenuItem disabled>
            <ListItemText>On totals, set by the calculated field</ListItemText>
          </MenuItem>
        )}
        {menu?.kind === 'aggregate' &&
          menu.locked === 'countDistinct' && [
            ...AGGREGATE_OPTIONS.map((option) => (
              <MenuItem disabled key={option.value}>
                <ListItemIcon>
                  {option.value === 'countDistinct' && <CheckIcon fontSize="small" sx={{ color: tokens.accent }} />}
                </ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            )),
            <Divider key="divider" />,
            <MenuItem disabled key="hint">
              <ListItemText secondary="Ids are counted, not summed" />
            </MenuItem>,
          ]}
        {menu?.kind === 'aggregate' &&
          !menu.locked &&
          AGGREGATE_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                state.setAggregate(option.value);
                setMenuAnchor(null);
              }}
              selected={option.value === currentAggregate}
            >
              <ListItemIcon>
                {option.value === currentAggregate && <CheckIcon fontSize="small" sx={{ color: tokens.accent }} />}
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip}>
      <span>{body}</span>
    </Tooltip>
  ) : (
    body
  );
}
